/**
 * The in-flight turn's PROMPT (its user message), held outside the React tree.
 *
 * op-summarize writes a turn's user AND assistant rows in one transaction when the turn ENDS
 * (`commit_turn`), and the replayed event log carries no user-message event at all — so while a turn is in
 * flight its prompt exists nowhere but this client. Kelly wipes `liveMessages` on nav-away and loses the whole
 * controller when the route unmounts, so resume rebuilt the answer with no question above it (APP-3376).
 * A module-level store (not a ref) is what survives that unmount; entries are dropped the moment the turn
 * commits (history owns the prompt from then on) or its stream is gone (nothing left to resume).
 */

import type { ChatMessage } from "@mui/x-chat-headless"
import { v4 } from "uuid"

/** A prompt whose turn is still in flight. `id` is minted here so its optimistic bubble keeps one identity across visits. */
export interface PendingUserTurn {
  id: string
  text: string
  createdAt: string
}

/** Key for a turn sent from New chat — re-keyed onto the real id when `thread_created` mints it. */
export const PENDING_NEW_THREAD_KEY = "__new-chat__"

// One entry per in-flight thread; dropped on commit or stream-gone. navigateToThread does NOT clear an entry —
// a thread whose turn starts and then never resolves leaks its entry for the session. Intentional and bounded:
// at most one entry per ever-opened thread (O(n) open threads), cleaned up as each stream closes.
const pendingTurns = new Map<string, PendingUserTurn>()

/** The pending prompt of a thread (or of the not-yet-created New chat), or null when its turn isn't in flight. */
export function getPendingUserTurn(key: string | null): PendingUserTurn | null {
  return key == null ? null : pendingTurns.get(key) ?? null
}

/** Park a just-sent prompt against the thread it was sent on, replacing any earlier entry for that thread. */
export function rememberPendingUserTurn(key: string, text: string): PendingUserTurn {
  const turn: PendingUserTurn = { id: v4(), text, createdAt: new Date().toISOString() }
  pendingTurns.set(key, turn)
  return turn
}

/** Move the New-chat entry onto the id `thread_created` just minted, so a resume probe on that thread finds it. */
export function adoptPendingUserTurn(threadId: string): void {
  const turn = pendingTurns.get(PENDING_NEW_THREAD_KEY)
  if (turn == null) return
  pendingTurns.delete(PENDING_NEW_THREAD_KEY)
  pendingTurns.set(threadId, turn)
}

export function forgetPendingUserTurn(key: string): void {
  pendingTurns.delete(key)
}

/** Test seam: the store deliberately outlives any component tree, so tests must clear it between cases. */
export function resetPendingUserTurns(): void {
  pendingTurns.clear()
}

/** The optimistic bubble for a pending prompt, in the controlled array's own vocabulary. */
export function pendingUserChatMessage(turn: PendingUserTurn): ChatMessage {
  return {
    id: turn.id,
    role: "user",
    parts: [{ type: "text", text: turn.text }],
    createdAt: turn.createdAt,
    status: "sent",
  }
}
